'use client'

import { useEffect, useId, useRef, useState, type RefObject } from 'react'
import Image from 'next/image'

import { halloweenPools, rockCharacters } from '@/lib/halloween-art'

import './halloween.css'

type Character = keyof typeof rockCharacters
type LogoColour = 'brand' | 'moonglow' | 'slime'
// Expressions reserve their character, so calm and laughing copies cannot appear together.
const identity = (source: string) => source.replace(/-(calm|laughing)\.svg$/, '.svg')
const reservations = new Map<HTMLElement, string>()
const hostSelector = '[data-halloween-host], h1, h2, h3, button, a, blockquote, article, section'

function hashFraction(value: string) {
  let hash = 2166136261
  for (const char of value) hash = Math.imul(hash ^ char.charCodeAt(0), 16777619)
  return (hash >>> 0) / 0x100000000
}

// A host taller than the screen never leaves it, so a decoration inside one would never re-roll.
function pickHost(el: HTMLElement) {
  const host = el.closest<HTMLElement>(hostSelector) ?? el
  const tall = host.getBoundingClientRect().height > innerHeight * 1.2
  return tall ? (el.parentElement ?? host) : host
}

function uniqueArtwork(assets: readonly string[]) {
  return [...new Map(assets.map((source) => [identity(source), source])).values()]
}

function useGroupedPresence(appearanceChance: number, vanishChance: number) {
  const ref = useRef<HTMLSpanElement>(null)
  const id = useId()
  const [visible, setVisible] = useState(() => hashFraction(`${id}:initial`) < appearanceChance)
  const visibleRef = useRef(visible)

  useEffect(() => {
    const current = ref.current
    if (!current) return
    const el: HTMLSpanElement = current
    const reviewAll =
      process.env.NODE_ENV !== 'production' &&
      new URLSearchParams(window.location.search).get('halloween-review') === 'all'
    if (reviewAll) {
      let active = true
      queueMicrotask(() => {
        if (!active) return
        visibleRef.current = true
        setVisible(true)
      })
      return () => {
        active = false
      }
    }
    const host = el
    const motion = matchMedia('(prefers-reduced-motion: reduce)')
    let timer: ReturnType<typeof setTimeout> | undefined
    let visited = false
    let generation = 0

    const setPresence = (next: boolean) => {
      visibleRef.current = next
      setVisible(next)
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        clearTimeout(timer)
        generation += 1
        const visit = generation
        if (entry.isIntersecting) {
          visited = true
          return
        }
        if (!visited || motion.matches) return
        timer = setTimeout(() => {
          if (document.hidden || motion.matches || generation !== visit) return
          if (visibleRef.current) {
            const roll = Math.random()
            if (roll < vanishChance) setPresence(false)
          } else if (Math.random() < appearanceChance) {
            setPresence(true)
          }
        }, 375)
      },
      { rootMargin: '120px' }
    )
    observer.observe(host)
    return () => {
      clearTimeout(timer)
      generation += 1
      observer.disconnect()
    }
  }, [appearanceChance, vanishChance])

  return { ref: ref as RefObject<HTMLSpanElement>, visible }
}

/** Artwork stages around its semantic host and changes only while that host is out of view. */
export function HalloweenPrankster({
  assets = halloweenPools.all,
  character,
  initial = 0,
  className = '',
  appearanceChance = 0.2,
  changeChance = 0.2,
  vanishChance = 0.4,
  mischievous,
}: {
  assets?: readonly string[]
  character?: Character
  initial?: number
  className?: string
  appearanceChance?: number
  changeChance?: number
  vanishChance?: number
  mischievous?: boolean
}) {
  const cast = character ? rockCharacters[character] : assets
  const start = character ? 0 : initial % cast.length
  const id = useId()
  const ref = useRef<HTMLSpanElement>(null)
  const [index, setIndex] = useState(start)
  const [pose, setPose] = useState(0)
  const initiallyHidden = hashFraction(`${id}:appearance`) >= appearanceChance
  const [hiding, setHiding] = useState(initiallyHidden)
  const effectiveVanishChance = mischievous === false ? 0 : vanishChance

  useEffect(() => {
    const currentElement = ref.current
    if (!currentElement || !cast.length) return
    const el: HTMLSpanElement = currentElement
    const reviewAll =
      process.env.NODE_ENV !== 'production' &&
      new URLSearchParams(window.location.search).get('halloween-review') === 'all'
    if (reviewAll) {
      let active = true
      queueMicrotask(() => {
        if (active) setHiding(false)
      })
      return () => {
        active = false
      }
    }
    const motion = matchMedia('(prefers-reduced-motion: reduce)')
    const host = pickHost(el)
    let timer: ReturnType<typeof setTimeout> | undefined
    let current = start
    let nearby = false
    let hasVisited = false
    let generation = 0
    let pendingImage: HTMLImageElement | undefined
    let hidden = initiallyHidden
    const setHidden = (next: boolean) => {
      hidden = next
      setHiding(next)
    }

    function takenArtwork() {
      const taken = new Set(
        [...reservations].filter(([host]) => host !== el).map(([, source]) => source)
      )
      return taken
    }

    function availableArtwork(excludeCurrent: boolean) {
      const taken = takenArtwork()
      return uniqueArtwork(cast)
        .filter(
          (source) =>
            !taken.has(identity(source)) &&
            (!excludeCurrent || identity(source) !== identity(cast[current]))
        )
        .map((source) => cast.indexOf(source))
    }

    function reserveCurrent() {
      if (hidden) return
      const source = identity(cast[current])
      if (!takenArtwork().has(source)) reservations.set(el, source)
    }

    function stageFirstVisit() {
      if (hidden) return
      const taken = takenArtwork()
      const currentSource = identity(cast[current])
      const options = availableArtwork(false)

      if (taken.has(currentSource) && options.length) {
        current = options[Math.floor(Math.random() * options.length)]
        setIndex(current)
      }
      if (!taken.has(identity(cast[current]))) reservations.set(el, identity(cast[current]))
    }

    function cancelPending() {
      if (pendingImage) pendingImage.src = ''
      pendingImage = undefined
    }

    function commitArtwork(nextIndex: number, changeId: number, show: boolean) {
      if (generation !== changeId || nearby || document.hidden || motion.matches) return
      current = nextIndex
      setIndex(nextIndex)
      setHidden(!show)
      if (!character) setPose(Math.floor(Math.random() * 5) - 2)
      pendingImage = undefined
    }

    function loadArtwork(nextIndex: number, changeId: number, show: boolean) {
      if (nextIndex === current) {
        if (show) {
          reservations.set(el, identity(cast[current]))
          setHidden(false)
        }
        return
      }

      reservations.set(el, identity(cast[nextIndex]))
      const image = new window.Image()
      pendingImage = image
      image.src = cast[nextIndex]
      const onReady = () => commitArtwork(nextIndex, changeId, show)
      if (typeof image.decode !== 'function') {
        onReady()
        return
      }
      void image
        .decode()
        .then(onReady)
        .catch(() => {
          if (generation !== changeId || nearby) return
          reservations.delete(el)
          if (!hidden) reserveCurrent()
          pendingImage = undefined
        })
    }

    function visitWhileAway(changeId: number) {
      if (document.hidden || motion.matches || nearby || generation !== changeId) return

      if (hidden) {
        if (Math.random() >= appearanceChance) return
        const options = availableArtwork(false)
        if (!options.length) return
        const nextIndex = options[Math.floor(Math.random() * options.length)]
        // Rock guys always arrive calm; the laugh is rolled while they are offscreen.
        loadArtwork(character ? 0 : nextIndex, changeId, true)
        return
      }

      if (Math.random() < effectiveVanishChance) {
        reservations.delete(el)
        setHidden(true)
        return
      }

      if (character) {
        const laughs = Math.random() < (current === 1 ? 0.6 : 0.5)
        loadArtwork(laughs ? 1 : 0, changeId, true)
        return
      }

      if (Math.random() >= changeChance) return
      const options = availableArtwork(true)
      if (!options.length) return
      loadArtwork(options[Math.floor(Math.random() * options.length)], changeId, true)
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        clearTimeout(timer)
        timer = undefined
        generation += 1
        cancelPending()
        nearby = entry.isIntersecting
        if (entry.isIntersecting) {
          // Away copies may have rolled the same art; whoever arrives second picks again.
          stageFirstVisit()
          hasVisited = true
        } else {
          reservations.delete(el)
        }
        if (entry.isIntersecting || motion.matches || !hasVisited) return
        const changeId = generation
        timer = setTimeout(() => {
          visitWhileAway(changeId)
        }, 375)
      },
      { rootMargin: '120px' }
    )
    observer.observe(host)
    return () => {
      clearTimeout(timer)
      generation += 1
      cancelPending()
      observer.disconnect()
      reservations.delete(el)
    }
  }, [appearanceChance, cast, character, changeChance, effectiveVanishChance, start])

  return (
    <span
      ref={ref}
      aria-hidden="true"
      data-hiding={hiding}
      data-character={character}
      data-expression={character ? (index === 1 ? 'laughing' : 'calm') : undefined}
      className={`spoopy-prankster ${className}`}
      style={{ transform: `rotate(${pose * 3}deg)` }}
    >
      <Image src={cast[index]} alt="" fill sizes="(max-width: 640px) 80px, 160px" unoptimized />
    </span>
  )
}

/** Exact supplied artwork, with offscreen colour changes and a rare 200ms Vampire glimpse. */
export function HalloweenLogo({
  format,
  colour: initialColour = 'moonglow',
}: {
  format: 'hero-linear' | 'hero-stacked' | 'hero-alt-stack' | 'hero-shorthand'
  colour?: LogoColour
}) {
  const ref = useRef<HTMLSpanElement>(null)
  const [colour, setColour] = useState<LogoColour>(initialColour)
  const [vampire, setVampire] = useState(false)
  useEffect(() => {
    const motion = matchMedia('(prefers-reduced-motion: reduce)')
    const el = ref.current
    if (!el) return
    const arrival = requestAnimationFrame(() => {
      setColour((['brand', 'moonglow', 'slime'] as const)[Math.floor(Math.random() * 3)])
    })
    if (motion.matches) return () => cancelAnimationFrame(arrival)
    let timer: ReturnType<typeof setTimeout>
    let reset: ReturnType<typeof setTimeout>
    let offscreen: ReturnType<typeof setTimeout>
    function haunt() {
      timer = setTimeout(
        () => {
          const bounds = el?.getBoundingClientRect()
          if (
            !document.hidden &&
            !motion.matches &&
            bounds &&
            bounds.bottom > 0 &&
            bounds.top < innerHeight
          ) {
            setVampire(true)
            reset = setTimeout(() => setVampire(false), 200)
          }
          haunt()
        },
        44000 + Math.random() * 70000
      )
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        clearTimeout(offscreen)
        if (!entry.isIntersecting)
          offscreen = setTimeout(() => {
            if (motion.matches || document.hidden) return
            setColour((previous) => {
              const alternatives = (['brand', 'moonglow', 'slime'] as const).filter(
                (next) => next !== previous
              )
              return alternatives[Math.floor(Math.random() * alternatives.length)]
            })
          }, 450)
      },
      { rootMargin: '120px' }
    )
    observer.observe(el)
    function settle() {
      if (motion.matches) setVampire(false)
    }
    motion.addEventListener('change', settle)
    haunt()
    return () => {
      clearTimeout(timer)
      clearTimeout(reset)
      cancelAnimationFrame(arrival)
      clearTimeout(offscreen)
      observer.disconnect()
      motion.removeEventListener('change', settle)
    }
  }, [])
  return (
    <span
      ref={ref}
      className="spoopy-logo-art"
      aria-hidden="true"
      data-vampire={vampire}
      data-colour={colour}
    >
      <Image
        className="spoopy-logo-layer"
        data-visible={!vampire}
        src={`/halloween/logos/${colour}/${format}.svg`}
        fill
        alt=""
        unoptimized
        sizes="300px"
      />
      <Image
        className="spoopy-logo-layer"
        data-visible={vampire}
        src={`/halloween/logos/vampire/${format}.svg`}
        fill
        alt=""
        unoptimized
        sizes="300px"
      />
    </span>
  )
}

/** A contextual cast lives inside its host component and reserves a predictable footprint. */
export function HalloweenTrinket({
  pool = 'all',
  assets,
  character,
  initial = 0,
  className = '',
  canHide,
  appearanceChance = 0.2,
  changeChance = 0.2,
  vanishChance = 0.4,
}: {
  pool?: keyof typeof halloweenPools
  assets?: readonly string[]
  character?: Character
  initial?: number
  className?: string
  canHide?: boolean
  appearanceChance?: number
  changeChance?: number
  vanishChance?: number
}) {
  const id = useId()
  const cast = assets ?? halloweenPools[pool]
  const selection = uniqueArtwork(cast)
  const seededArtwork =
    selection[Math.floor(hashFraction(`${id}:${initial}:artwork`) * selection.length)]
  const seededIndex = Math.max(0, cast.indexOf(seededArtwork))
  return (
    <span className={`spoopy-trinket ${className}`} aria-hidden="true">
      <HalloweenPrankster
        assets={cast}
        character={character}
        initial={character ? 0 : seededIndex}
        appearanceChance={appearanceChance}
        changeChance={changeChance}
        vanishChance={vanishChance}
        mischievous={canHide}
      />
    </span>
  )
}

const slimeDrops = halloweenPools['slime-and-bits'].filter((path) => path.includes('/slime-drop-'))

/** The ten small goo assets are punctuation: a splashed trail, never a lone mascot. */
export function HalloweenSlimeTrail({
  className = '',
  compact = false,
}: {
  className?: string
  compact?: boolean
}) {
  const { ref, visible } = useGroupedPresence(0.2, 0.4)
  return (
    <span
      ref={ref}
      className={`spoopy-slime-trail ${className}`}
      aria-hidden="true"
      data-compact={compact || undefined}
      style={{ visibility: visible ? undefined : 'hidden' }}
    >
      {visible &&
        Array.from({ length: compact ? 3 : 7 }, (_, i) => (
          <span key={i} className="spoopy-slime-splash">
            <HalloweenPrankster
              assets={slimeDrops}
              initial={i}
              appearanceChance={1}
              changeChance={0.15}
              vanishChance={0}
              mischievous={false}
            />
          </span>
        ))}
    </span>
  )
}

/** Ghost hands emerge through pale sections; foreground content stays above them. */
export function HalloweenGhostHands({
  variant = 0,
  className = '',
}: {
  variant?: number
  className?: string
}) {
  const { ref, visible } = useGroupedPresence(0.2, 0.4)
  return (
    <span
      ref={ref}
      aria-hidden="true"
      className={`spoopy-ghost-hands ${className}`}
      data-variant={variant % 3}
      style={{ visibility: visible ? undefined : 'hidden' }}
    >
      {visible &&
        [0, 1].map((i) => (
          <span className="spoopy-emerging-hand" key={i}>
            <HalloweenPrankster
              assets={halloweenPools['ghost-hands']}
              initial={(variant * 2 + i) % halloweenPools['ghost-hands'].length}
              appearanceChance={1}
              changeChance={0.15}
              vanishChance={0}
              mischievous={false}
            />
          </span>
        ))}
    </span>
  )
}
